import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Chip,
    CircularProgress,
    Tooltip,
    InputAdornment,
    Paper,
    TableContainer,
    Divider,
    IconButton
} from "@mui/material";
import {
    Search as SearchIcon,
    CheckCircleOutline as CleanIcon,
    CleaningServices as BroomIcon,
    Hotel as HotelIcon
} from "@mui/icons-material";
import { auth, db } from "../../firebase";
import {
    collection,
    doc,
    getDoc,
    onSnapshot,
    query,
    where,
    updateDoc
} from "firebase/firestore";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

function Roomcleanings() {
  const [propertyId, setPropertyId] = useState(null);
  const [roomsMap, setRoomsMap] = useState({});
  const [cleanRooms, setCleanRooms] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);

  // 1️⃣ FETCH PROPERTY ID FROM AUTHROLES
  useEffect(() => {
    return auth.onAuthStateChanged(async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "authroles", user.uid));
      if (snap.exists()) {
        setPropertyId(snap.data().propertyId);
      }
    });
  }, []);

  // 2️⃣ FETCH ROOMINFOS BY PROPERTY ID
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "roominfos"),
      where("propertyId", "==", propertyId)
    );

    return onSnapshot(q, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setRoomsMap(map);
    });
  }, [propertyId]);

  // 3️⃣ FETCH CLEANING ROOMS BY PROPERTY
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "cleaningrooms"),
      where("status", "==", "Cleaning")
    );

    return onSnapshot(q, (snap) => {
      setCleanRooms(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }))
      );
      setLoading(false);
    });
  }, [propertyId]);

  // 4️⃣ JOIN & FILTER BY PROPERTY
  const filteredRooms = cleanRooms
    .filter((r) => roomsMap[r.roomid]) 
    .filter((r) =>
      r.roomno.toLowerCase().includes(searchText.toLowerCase())
    );

  // UPDATE CLEANINGROOM + ROOMINFO STATUS
  const markAsCleaned = async (cleaningRoom) => {
    try {
      const user = auth.currentUser;

      await updateDoc(doc(db, "cleaningrooms", cleaningRoom.id), {
        status: "Cleaned",
        cleanedby: user?.email || user?.uid || "",
        cleanedtime: new Date().toLocaleString()
      });

      await updateDoc(doc(db, "roominfos", cleaningRoom.roomid), {
        status: "Available"
      });
    } catch (error) {
      console.error("Error updating room status:", error);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
        
        {/* Compact Header Section */}
        <Box display="flex" alignItems="center" gap={1.5} mb={3}>
          <Box 
            sx={{ 
              backgroundColor: colors.calPolyGreen, 
              p: 1, 
              borderRadius: "8px", 
              border: `1px solid ${colors.goldenrod}`,
              display: "flex",
              alignItems: "center"
            }}
          >
            <BroomIcon sx={{ color: colors.gold, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: colors.vanilla, lineHeight: 1.2, fontSize: '1.1rem' }}>
              Housekeeping Dashboard
            </Typography>
            <Typography variant="caption" sx={{ color: colors.goldenrod, opacity: 0.9 }}>
              Real-time maintenance updates
            </Typography>
          </Box>
        </Box>

        {/* Compact Core Container Card */}
        <Card 
          elevation={0} 
          sx={{ 
            borderRadius: 3, 
            border: `1px solid ${colors.goldenrod}`, 
            bgcolor: colors.calPolyGreen, 
            color: colors.vanilla, 
            boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)"
          }}
        >
          <CardContent sx={{ p: 3 }}>
            
            {/* Compact Action Bar */}
            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
              <TextField
                size="small"
                placeholder="Search room..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{
                  style: { color: colors.vanilla },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: colors.goldenrod, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: 240,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: colors.darkGreen,
                    fontSize: "0.85rem",
                    height: "38px",
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  }
                }}
              />
              <Chip 
                label={`${filteredRooms.length} Pending`} 
                variant="outlined" 
                size="small"
                sx={{ fontWeight: 600, color: colors.vanilla, borderColor: colors.goldenrod, bgcolor: colors.darkGreen, height: "26px", fontSize: "0.75rem" }}
              />
            </Box>

            <Divider sx={{ mb: 3, borderColor: colors.goldenrod, opacity: 0.4 }} />

            {loading ? (
              <Box textAlign="center" py={4}>
                <CircularProgress color="primary" size={24} thickness={4} sx={{ color: colors.gold }} />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: colors.darkGreen }}>
                    <TableRow>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, py: 1.2, fontSize: "0.75rem", width: "40px" }}>#</TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, py: 1.2, fontSize: "0.75rem" }}>Room</TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, py: 1.2, fontSize: "0.75rem" }}>Type</TableCell>
                      <TableCell sx={{ color: colors.vanilla, fontWeight: 700, py: 1.2, fontSize: "0.75rem", width: "140px" }}>Status</TableCell>
                      <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, py: 1.2, pr: 2, fontSize: "0.75rem", width: "60px" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredRooms.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7, fontSize: "0.8rem" }}>
                          No active rooms require cleaning.
                        </TableCell>
                      </TableRow>
                    )}

                    {filteredRooms.map((room, index) => {
                      const roomInfo = roomsMap[room.roomid];

                      return (
                        <TableRow 
                          key={room.id} 
                          hover 
                          sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }, "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell sx={{ color: colors.vanilla, py: 1.2, fontSize: "0.8rem", borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{index + 1}</TableCell>

                          <TableCell sx={{ py: 1.2, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <HotelIcon sx={{ color: colors.goldenrod, fontSize: 20 }} />
                              <Typography fontWeight={600} sx={{ color: colors.gold, fontSize: "0.85rem" }}>
                                {room.roomno}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell sx={{ color: colors.vanilla, py: 1.2, fontSize: "0.8rem", borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            {roomInfo?.roomTypeName || "—"}
                          </TableCell>

                          <TableCell sx={{ py: 1.2, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            <Chip
                              label="Cleaning"
                              size="small"
                              sx={{
                                backgroundColor: colors.darkGreen,
                                color: colors.gold,
                                fontWeight: 600,
                                border: `1px solid ${colors.goldenrod}`,
                                fontSize: "0.7rem",
                                height: "22px"
                              }}
                            />
                          </TableCell>

                          <TableCell align="right" sx={{ py: 1.2, pr: 1.5, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                            <Tooltip title="Mark Cleaned" arrow placement="top">
                              <IconButton
                                onClick={() => markAsCleaned(room)}
                                size="small"
                                sx={{
                                  color: colors.darkGreen,
                                  backgroundColor: colors.goldenrod,
                                  p: 1,
                                  borderRadius: "8px",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: colors.gold,
                                    transform: "scale(1.05)"
                                  }
                                }}
                              >
                                <CleanIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default Roomcleanings;